import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  #abortController = null;
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = this.closest('product-card');
    const productLink = productCard?.getProductCardLink();
    if (!productLink?.href) return '';

    const url = new URL(productLink.href);
    if (url.searchParams.has('variant')) return url.toString();

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);
    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard = this.closest('product-card');
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  handleClick = async (event) => {
    event.preventDefault();
    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = gridElement.cloneNode(true);
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = productGrid.cloneNode(true);
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker, .custom-variant-picker-wrapper');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;
      if (!productFormComponent || !productPrice) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      if (variantPicker) productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);
  }

  #prepareFinalizeCheckout(modalContent) {
    const productFormComponent = modalContent.querySelector('product-form-component');
    const form = productFormComponent?.querySelector('form[action*="/cart/add"]');
    if (!(form instanceof HTMLFormElement)) return;

    const buttons = form.querySelector('.product-form-buttons');
    if (!buttons) return;

    const acceleratedContainer = buttons.querySelector('.accelerated-checkout-block');
    const existing = buttons.querySelector('.quick-add-finalize-checkout');
    if (existing) return;

    if (acceleratedContainer instanceof HTMLElement) {
      acceleratedContainer.replaceChildren();
      acceleratedContainer.hidden = false;
      acceleratedContainer.removeAttribute('ref');
    }

    const holder = acceleratedContainer instanceof HTMLElement ? acceleratedContainer : document.createElement('div');
    if (!(acceleratedContainer instanceof HTMLElement)) {
      holder.className = 'accelerated-checkout-block';
      buttons.appendChild(holder);
    }

    const finalizeButton = document.createElement('button');
    finalizeButton.type = 'button';
    finalizeButton.className = 'button quick-add-finalize-checkout';
    finalizeButton.textContent = 'Finalizar Compra';
    holder.appendChild(finalizeButton);
  }

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
    this.addEventListener('click', this.#handleNativeClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #handleNativeClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const closeButton = target.closest('.quick-add-modal__close');
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      this.closeDialog();
      return;
    }

    const finalizeButton = target.closest('.quick-add-finalize-checkout');
    if (finalizeButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#finalizeCheckout(finalizeButton);
    }
  };

  async #finalizeCheckout(button) {
    if (button.dataset.loading === 'true') return;

    const form = button.closest('.product-form-buttons')?.closest('form[action*="/cart/add"]');
    if (!(form instanceof HTMLFormElement)) return;

    const validatePersonalizacao = window.dropmetaValidatePersonalizacao;
    if (typeof validatePersonalizacao === 'function' && !validatePersonalizacao(form)) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    button.dataset.loading = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData(form);
      const response = await fetch(Theme.routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const payload = await response.json();
      if (!response.ok || payload?.status) {
        throw new Error(payload?.description || payload?.message || 'Não foi possível adicionar o produto.');
      }
      window.location.assign('/checkout');
    } catch (error) {
      console.error('Quick checkout error:', error);
      button.disabled = false;
      button.dataset.loading = 'false';
      button.removeAttribute('aria-busy');
      const liveRegion = form.closest('product-form-component')?.querySelector('[ref="liveRegion"]');
      if (liveRegion) liveRegion.textContent = error.message || 'Não foi possível finalizar a compra.';
    }
  }

  #updateProductTitleLink = (event) => {
    const anchorElement = event.detail.data.html?.querySelector('.view-product-title a');
    const viewMoreDetailsLink = this.querySelector('.view-product-title a');
    const mobileProductTitle = this.querySelector('.product-header a');
    if (!anchorElement) return;
    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
